import { useState, useEffect } from 'react';
import axios from 'axios';
import { useModal } from '../components/Modal';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function PackagePage({ user, dark, service, onBack }) {
  const { showModal } = useModal();
  const [activeTab, setActiveTab] = useState('buy');
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [link, setLink] = useState('');
  const [username, setUsername] = useState('');
  const [commentLink, setCommentLink] = useState('');
  const [comments, setComments] = useState('');
  const [customQty, setCustomQty] = useState('');
  const [creditBalance, setCreditBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showFullDesc, setShowFullDesc] = useState(false);

  const type = service.type || 'Default';
  const isAutoLike = type === 'Subscriptions';
  const isComments = type === 'Custom Comments';
  const unitLabel = service.unit_label || 'หน่วย';

  const customPkg = service.packages?.find(p => p.is_custom);
  const normalPkgs = service.packages?.filter(p => !p.is_custom) || [];
  const canUseCredit = creditBalance > 0 && !isAutoLike;

  const c = {
    bg: dark ? '#0d0d0d' : '#f8f8f8',
    card: dark ? '#1c1c1c' : '#ffffff',
    cardBorder: dark ? '#2e2e2e' : '#e8e8e8',
    text: dark ? '#eeeeee' : '#111111',
    sub: dark ? '#555555' : '#aaaaaa',
    inp: dark ? '#252525' : '#f5f5f5',
    inpBorder: dark ? '#383838' : '#e0e0e0',
    inpText: dark ? '#eeeeee' : '#111111',
  };

  useEffect(() => {
    axios.get(`${BACKEND}/credits/${service.id}?lineUserId=${user.userId}`)
      .then(res => setCreditBalance(Number(res.data.balance) || 0))
      .catch(() => setCreditBalance(0));
  }, [service.id, user.userId]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedPkg(null);
    setCustomQty('');
    setLink('');
    setCommentLink('');
    setComments('');
  };

  // ===== COMPUTED VALUES =====

  const customQtyNum = parseInt(customQty) || 0;
  const customMinQty = customPkg?.min_qty || 1;
  const commentLineCount = comments.split('\n').filter(c => c.trim()).length;

  const getOrderQty = () => {
    if (activeTab === 'use') {
      if (isComments) return commentLineCount;
      return customQtyNum;
    }
    // buy tab & autolike
    if (selectedPkg) return selectedPkg.units || 0;
    return 0;
  };

  const getOrderPrice = () => {
    if (activeTab === 'use') return 0; // ไม่มีราคา หักเครดิตโดยตรง
    if (selectedPkg) return selectedPkg.price || 0;
    return 0;
  };

  const customQtyValid = activeTab === 'use'
    ? (isComments
        ? (commentLineCount > 0 && commentLineCount <= creditBalance)
        : (customQtyNum >= customMinQty && customQtyNum <= creditBalance && customQtyNum > 0))
    : true;

  const canOrder = () => {
    if (activeTab === 'buy') {
      if (!selectedPkg) return false;
      if (isAutoLike && !username) return false;
      if (isComments && activeTab !== 'buy' && (!commentLink || !comments)) return false;
      return true;
    }
    // use tab
    if (!customPkg) return false;
    if (!customQtyValid) return false;
    if (isComments && (!commentLink || !comments)) return false;
    if (!isComments && !link) return false;
    return true;
  };

  // ===== HANDLE ORDER =====

  const handleOrder = async () => {
    if (!canOrder()) return;

    const qty = getOrderQty();
    if (qty <= 0) {
      showModal({ type: 'warning', title: 'ข้อมูลไม่ครบ', message: 'กรุณากรอกจำนวนค่ะ' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        lineUserId: user.userId,
        serviceId: service.id,
        panelServiceId: service.panel_service_id,
        type,
        quantity: qty,
      };

      if (activeTab === 'buy') {
        // ===== ซื้อแพ็ก: หักกระเป๋ากลาง → เติมเครดิต =====
        payload.packageId = selectedPkg.id;
        payload.isCustom = false;

        if (isAutoLike) {
          // AutoLike: สั่งตรงไป panel เลย (ไม่มี save mode)
          payload.mode = 'now';
          payload.username = username;
          payload.min = selectedPkg.min_qty;
          payload.max = selectedPkg.max_qty;
        } else if (isComments) {
          // Comments ซื้อแพ็ก: save mode (เติมเครดิตก่อน)
          payload.mode = 'save';
        } else {
          // Default ซื้อแพ็ก: save mode
          payload.mode = 'save';
        }
      } else {
        // ===== ใช้เครดิต: หักเครดิต → สั่ง panel =====
        payload.packageId = customPkg?.id || normalPkgs[0]?.id;
        payload.isCustom = true;
        payload.mode = 'now';

        if (isComments) {
          payload.link = commentLink;
          payload.comments = comments.split('\n').filter(c => c.trim());
        } else {
          payload.links = [{ url: link, quantity: qty }];
        }
      }

      const res = await axios.post(`${BACKEND}/order`, payload);
      if (res.data.success) {
        setResult({ ...res.data, tab: activeTab });
      } else if (res.data.insufficientWallet) {
        showModal({ type: 'error', title: 'เงินในกระเป๋าไม่พอ', message: 'กรุณาเติมเงินก่อนค่ะ' });
      } else {
        showModal({ type: 'error', title: 'สั่งไม่สำเร็จ', message: res.data.message || 'เกิดข้อผิดพลาดค่ะ กรุณาลองใหม่ค่ะ' });
      }
    } catch (err) {
      showModal({ type: 'error', title: 'เชื่อมต่อไม่สำเร็จ', message: 'ระบบขัดข้องชั่วคราวค่ะ กรุณาลองใหม่อีกครั้งค่ะ' });
    }
    setLoading(false);
  };

  // ===== SUCCESS SCREEN =====

  if (result?.success) {
    const isBuy = result.tab === 'buy';
    return (
      <div style={{padding:'20px',display:'flex',flexDirection:'column',alignItems:'center',gap:'12px',background:c.bg,minHeight:'100vh',fontFamily:'Prompt,sans-serif'}}>
        <div style={{width:'60px',height:'60px',borderRadius:'50%',background:dark?'#1a0800':'#fff5f0',border:'2px solid #FF4500',display:'flex',alignItems:'center',justifyContent:'center',marginTop:'40px'}}>
          <i className={`ti ${isBuy ? 'ti-wallet' : 'ti-check'}`} style={{fontSize:'28px',color:'#FF4500'}} aria-hidden="true"/>
        </div>
        <p style={{fontSize:'16px',fontWeight:'700',color:c.text}}>
          {isBuy ? 'เติมเครดิตสำเร็จแล้วค่ะ!' : 'สั่งสำเร็จแล้วค่ะ!'}
        </p>
        <p style={{fontSize:'12px',color:c.sub,textAlign:'center'}}>
          {isBuy
            ? `เครดิต ${getOrderQty().toLocaleString()} ${unitLabel} ถูกเติมเข้าบัญชีแล้วค่ะ`
            : 'ระบบกำลังดำเนินการค่ะ'
          }
        </p>
        {result.partial && result.message && (
          <p style={{fontSize:'11px',color:'#f59e0b',textAlign:'center',background:dark?'#2a1f00':'#fffbeb',border:'1px solid #f59e0b',borderRadius:'8px',padding:'8px 12px',whiteSpace:'pre-wrap'}}>
            {result.message}
          </p>
        )}
        <button onClick={onBack} style={{width:'100%',padding:'12px',border:'none',borderRadius:'10px',background:'#FF4500',color:'#fff',fontSize:'13px',fontWeight:'600',cursor:'pointer',fontFamily:'Prompt,sans-serif'}}>
          กลับหน้าหลัก
        </button>
      </div>
    );
  }

  // ===== MAIN RENDER =====

  return (
    <div style={{padding:'12px',display:'flex',flexDirection:'column',gap:'10px',background:c.bg,minHeight:'100vh',fontFamily:'Prompt,sans-serif'}}>

      {/* ปุ่มกลับ + ชื่อ service */}
      <button onClick={onBack} style={{display:'flex',alignItems:'center',gap:'6px',background:'none',border:'none',cursor:'pointer',color:c.sub,padding:'4px 0',fontFamily:'Prompt,sans-serif',fontSize:'13px'}}>
        <i className="ti ti-arrow-left" style={{fontSize:'16px'}} aria-hidden="true"/>
        กลับ
      </button>

      <div>
        <p style={{fontSize:'15px',fontWeight:'700',color:c.text,margin:0}}>{service.name}</p>
        {service.short_desc && (
          <p style={{fontSize:'11px',color:c.sub,margin:'3px 0 0',lineHeight:1.5}}>{service.short_desc}</p>
        )}
        {service.description && (
          <button onClick={() => setShowFullDesc(true)}
            style={{background:'none',border:'none',cursor:'pointer',color:'#FF4500',fontSize:'10.5px',fontWeight:'600',padding:0,marginTop:'4px',fontFamily:'Prompt,sans-serif',display:'flex',alignItems:'center',gap:'3px'}}>
            ⚠️ ควรอ่านก่อนสั่งซื้อ
            <i className="ti ti-chevron-right" style={{fontSize:'11px'}} aria-hidden="true"/>
          </button>
        )}
      </div>

      {/* เครดิตคงเหลือ */}
      {creditBalance > 0 && (
        <div style={{background:dark?'#0d2614':'#eafff0',border:'1px solid #22c55e',borderRadius:'10px',padding:'8px 12px',display:'flex',alignItems:'center',gap:'8px'}}>
          <i className="ti ti-wallet" style={{fontSize:'15px',color:'#22c55e',flexShrink:0}} aria-hidden="true"/>
          <p style={{fontSize:'11.5px',color:dark?'#86efac':'#15803d',margin:0}}>
            คุณมี <b>{creditBalance.toLocaleString()} {unitLabel}</b> เหลืออยู่ค่ะ
          </p>
        </div>
      )}

      {/* ===== แท็บ ซื้อแพ็ก / ใช้เครดิต (ไม่แสดงสำหรับ AutoLike) ===== */}
      {!isAutoLike && (
        <div style={{display:'grid',gridTemplateColumns: canUseCredit ? '1fr 1fr' : '1fr',gap:'8px'}}>
          <button onClick={() => handleTabChange('buy')}
            style={{
              padding:'10px 8px',border:`1.5px solid ${activeTab==='buy'?'#FF4500':c.inpBorder}`,
              borderRadius:'10px',background:activeTab==='buy'?(dark?'#1a0800':'#fff5f0'):c.inp,
              cursor:'pointer',textAlign:'center',fontFamily:'Prompt,sans-serif',
            }}>
            <i className="ti ti-shopping-cart" style={{fontSize:'16px',color:activeTab==='buy'?'#FF4500':c.sub}} aria-hidden="true"/>
            <p style={{fontSize:'12px',fontWeight:'700',color:activeTab==='buy'?'#FF4500':c.text,margin:'4px 0 2px'}}>ซื้อแพ็ก</p>
            <p style={{fontSize:'9.5px',color:c.sub,margin:0}}>จ่ายจากกระเป๋ากลาง</p>
          </button>
          {canUseCredit && (
            <button onClick={() => handleTabChange('use')}
              style={{
                padding:'10px 8px',border:`1.5px solid ${activeTab==='use'?'#FF4500':c.inpBorder}`,
                borderRadius:'10px',background:activeTab==='use'?(dark?'#1a0800':'#fff5f0'):c.inp,
                cursor:'pointer',textAlign:'center',fontFamily:'Prompt,sans-serif',
              }}>
              <i className="ti ti-bolt" style={{fontSize:'16px',color:activeTab==='use'?'#FF4500':c.sub}} aria-hidden="true"/>
              <p style={{fontSize:'12px',fontWeight:'700',color:activeTab==='use'?'#FF4500':c.text,margin:'4px 0 2px'}}>ใช้เครดิต</p>
              <p style={{fontSize:'9.5px',color:c.sub,margin:0}}>{creditBalance.toLocaleString()} {unitLabel} คงเหลือ</p>
            </button>
          )}
        </div>
      )}

      {/* ===== แท็บ "ซื้อแพ็ก" หรือ AutoLike ===== */}
      {(activeTab === 'buy' || isAutoLike) && (
        <>
          {/* เลือกแพ็ก */}
          <div style={{background:c.card,border:`1px solid ${c.cardBorder}`,borderRadius:'12px',padding:'12px'}}>
            <p style={{fontSize:'11px',color:c.sub,marginBottom:'8px'}}>
              {isAutoLike ? 'เลือกแพ็ก (จำนวนโพสต์)' : 'เลือกแพ็ก'}
            </p>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
              {normalPkgs.map(pkg => (
                <div key={pkg.id} onClick={() => setSelectedPkg(pkg)}
                  style={{
                    padding:'10px 8px',textAlign:'center',
                    background:selectedPkg?.id===pkg.id?(dark?'#1a0800':'#fff5f0'):c.inp,
                    border:`1.5px solid ${selectedPkg?.id===pkg.id?'#FF4500':c.inpBorder}`,
                    borderRadius:'10px',cursor:'pointer'
                  }}>
                  <p style={{fontSize:'14px',fontWeight:'700',color:c.text,margin:0}}>
                    {pkg.units?.toLocaleString()}{isAutoLike ? ' โพสต์' : ` ${unitLabel}`}
                  </p>
                  <p style={{fontSize:'12px',color:'#FF4500',fontWeight:'600',margin:'2px 0 0'}}>฿{pkg.price}</p>
                  {isAutoLike && pkg.min_qty && (
                    <p style={{fontSize:'9px',color:c.sub,margin:'2px 0 0'}}>{pkg.min_qty}-{pkg.max_qty} like/โพสต์</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* AutoLike: username */}
          {isAutoLike && (
            <div style={{background:c.card,border:`1px solid ${c.cardBorder}`,borderRadius:'12px',padding:'12px',display:'flex',flexDirection:'column',gap:'8px'}}>
              <p style={{fontSize:'11px',color:c.sub,margin:0}}>Username Instagram</p>
              <input value={username} onChange={e => setUsername(e.target.value)}
                placeholder="username (ไม่ต้องใส่ @)"
                style={{background:c.inp,border:`1px solid ${c.inpBorder}`,borderRadius:'8px',padding:'8px 10px',fontSize:'12px',color:c.inpText,fontFamily:'Prompt,sans-serif',width:'100%',boxSizing:'border-box'}}
              />
            </div>
          )}

          {/* ซื้อแพ็ก: ข้อความแจ้ง (ไม่ใช่ AutoLike) */}
          {!isAutoLike && selectedPkg && (
            <div style={{background:dark?'#0d1a2e':'#eff6ff',border:'1px solid #3b82f6',borderRadius:'10px',padding:'10px 12px',display:'flex',alignItems:'flex-start',gap:'8px'}}>
              <i className="ti ti-info-circle" style={{fontSize:'15px',color:'#3b82f6',flexShrink:0,marginTop:'1px'}} aria-hidden="true"/>
              <p style={{fontSize:'11px',color:dark?'#93c5fd':'#1d4ed8',margin:0,lineHeight:1.5}}>
                เครดิต <b>{selectedPkg.units?.toLocaleString()} {unitLabel}</b> จะถูกเติมเข้าบัญชีของคุณทันทีค่ะ แล้วกดแท็บ "ใช้เครดิต" เพื่อสั่งงานได้เลย
              </p>
            </div>
          )}
        </>
      )}

      {/* ===== แท็บ "ใช้เครดิต" ===== */}
      {activeTab === 'use' && !isAutoLike && (
        <>
          {/* Default: กรอกจำนวน */}
          {!isComments && (
            <div style={{background:c.card,border:`1px solid ${c.cardBorder}`,borderRadius:'12px',padding:'12px',display:'flex',flexDirection:'column',gap:'8px'}}>
              <p style={{fontSize:'11px',color:c.sub,margin:0}}>จำนวนที่ต้องการ ({unitLabel})</p>
              <input value={customQty} onChange={e => setCustomQty(e.target.value)}
                type="number" placeholder={`ขั้นต่ำ ${customMinQty.toLocaleString()} ${unitLabel}`}
                style={{background:c.inp,border:`1px solid ${customQty && !customQtyValid?'#f87171':c.inpBorder}`,borderRadius:'8px',padding:'9px 10px',fontSize:'13px',color:c.inpText,fontFamily:'Prompt,sans-serif',width:'100%',boxSizing:'border-box'}}
              />
              {customQty && (
                <p style={{fontSize:'10px',margin:0,color:
                  customQtyNum < customMinQty ? '#f87171' :
                  customQtyNum > creditBalance ? '#f87171' : '#22c55e'
                }}>
                  {customQtyNum < customMinQty
                    ? `⚠️ ขั้นต่ำ ${customMinQty.toLocaleString()} ${unitLabel}ค่ะ`
                    : customQtyNum > creditBalance
                    ? `⚠️ เครดิตไม่พอค่ะ มีแค่ ${creditBalance.toLocaleString()} ${unitLabel}`
                    : `✅ หักจากเครดิต ${customQtyNum.toLocaleString()} ${unitLabel} (เหลือ ${(creditBalance - customQtyNum).toLocaleString()} ${unitLabel})`
                  }
                </p>
              )}
            </div>
          )}

          {/* Comments: ลิงก์ + ข้อความคอมเมนต์ */}
          {isComments && (
            <div style={{background:c.card,border:`1px solid ${c.cardBorder}`,borderRadius:'12px',padding:'12px',display:'flex',flexDirection:'column',gap:'8px'}}>
              <p style={{fontSize:'11px',color:c.sub,margin:0}}>ลิงก์โพสต์</p>
              <input value={commentLink} onChange={e => setCommentLink(e.target.value)}
                placeholder="ลิงก์โพสต์ของคุณ"
                style={{background:c.inp,border:`1px solid ${c.inpBorder}`,borderRadius:'8px',padding:'8px 10px',fontSize:'12px',color:c.inpText,fontFamily:'Prompt,sans-serif',width:'100%',boxSizing:'border-box'}}
              />
              <p style={{fontSize:'11px',color:c.sub,margin:0}}>ข้อความคอมเมนต์ (1 บรรทัด = 1 คอมเมนต์)</p>
              <textarea value={comments} onChange={e => setComments(e.target.value)}
                placeholder={'สวยมากค่ะ\nน่ารักจัง\nสุดยอด'} rows={4}
                style={{background:c.inp,border:`1px solid ${c.inpBorder}`,borderRadius:'8px',padding:'8px 10px',fontSize:'12px',color:c.inpText,fontFamily:'Prompt,sans-serif',width:'100%',boxSizing:'border-box',resize:'vertical'}}
              />
              <p style={{fontSize:'11px',color:c.sub,margin:0}}>
                จำนวนคอมเมนต์: <b>{commentLineCount}</b>
              </p>
              {commentLineCount > 0 && (
                <p style={{fontSize:'10px',margin:0,color: commentLineCount > creditBalance ? '#f87171' : '#22c55e'}}>
                  {commentLineCount > creditBalance
                    ? `⚠️ เครดิตไม่พอค่ะ มีแค่ ${creditBalance.toLocaleString()} ${unitLabel}`
                    : `✅ หักจากเครดิต ${commentLineCount} ${unitLabel} (เหลือ ${(creditBalance - commentLineCount).toLocaleString()} ${unitLabel})`
                  }
                </p>
              )}
            </div>
          )}

          {/* Default: ลิงก์ */}
          {!isComments && (
            <div style={{background:c.card,border:`1px solid ${c.cardBorder}`,borderRadius:'12px',padding:'12px',display:'flex',flexDirection:'column',gap:'8px'}}>
              <p style={{fontSize:'11px',color:c.sub,margin:0}}>ลิงก์ที่ต้องการสั่ง</p>
              <input value={link} onChange={e => setLink(e.target.value)}
                placeholder="ลิงก์ของคุณ"
                style={{background:c.inp,border:`1px solid ${c.inpBorder}`,borderRadius:'8px',padding:'9px 10px',fontSize:'12px',color:c.inpText,fontFamily:'Prompt,sans-serif',width:'100%',boxSizing:'border-box'}}
              />
            </div>
          )}
        </>
      )}

      {/* สรุปออเดอร์ */}
      {((activeTab === 'buy' && selectedPkg) || (activeTab === 'use' && getOrderQty() > 0) || (isAutoLike && selectedPkg)) && (
        <div style={{background:dark?'#1a0800':'#fff5f0',border:'1px solid #FF4500',borderRadius:'10px',padding:'10px 12px'}}>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:'11px',color:dark?'#aa6600':'#cc4400',marginBottom:'4px'}}>
            <span>จำนวน</span>
            <span>{getOrderQty() > 0 ? `${getOrderQty().toLocaleString()} ${isAutoLike ? 'โพสต์' : unitLabel}` : '-'}</span>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:'11px',color:dark?'#aa6600':'#cc4400'}}>
              {activeTab === 'use' ? 'ชำระ' : 'ราคา'}
            </span>
            <span style={{fontSize:activeTab==='use'?'13px':'16px',fontWeight:'700',color:'#FF4500'}}>
              {activeTab === 'use'
                ? (getOrderQty() > 0 ? `หักเครดิต ${getOrderQty().toLocaleString()} ${unitLabel}` : '-')
                : (getOrderPrice() > 0 ? `฿${getOrderPrice().toFixed(2)}` : '-')
              }
            </span>
          </div>
        </div>
      )}

      {/* ปุ่มสั่ง */}
      <button onClick={handleOrder}
        disabled={loading || !canOrder()}
        style={{
          width:'100%',padding:'13px',border:'none',borderRadius:'11px',
          background:canOrder()?'#FF4500':'#555',
          color:'#fff',fontSize:'13px',fontWeight:'700',
          cursor:canOrder()?'pointer':'not-allowed',
          fontFamily:'Prompt,sans-serif'
        }}>
        {loading ? 'กำลังดำเนินการ...' : (activeTab === 'buy' && !isAutoLike ? 'ซื้อเลยค่ะ' : 'สั่งเลยค่ะ')}
      </button>

      {/* Modal รายละเอียดเพิ่มเติม */}
      {showFullDesc && (
        <div onClick={() => setShowFullDesc(false)}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'20px',fontFamily:'Prompt,sans-serif'}}>
          <div onClick={e => e.stopPropagation()}
            style={{background:c.card,borderRadius:'16px',padding:'20px',width:'100%',maxWidth:'340px',maxHeight:'75vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'12px',gap:'8px'}}>
              <p style={{fontSize:'14px',fontWeight:'700',color:c.text,margin:0}}>{service.name}</p>
              <button onClick={() => setShowFullDesc(false)}
                style={{background:'none',border:'none',color:c.sub,fontSize:'18px',cursor:'pointer',flexShrink:0,lineHeight:1}}>×</button>
            </div>
            <p style={{fontSize:'12px',color:c.text,lineHeight:1.7,margin:0,whiteSpace:'pre-wrap'}}>{service.description}</p>
          </div>
        </div>
      )}

    </div>
  );
}

export default PackagePage;